import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
    DEFAULT_SEO,
    SITE_NAME,
    TWITTER_HANDLE,
    INSTAGRAM_HANDLE,
    SHARE_IMAGE_ALT,
    buildPageTitle,
    getAbsoluteImageUrl,
    getCanonicalUrl,
    normalizeCanonicalPath,
} from '../seoConfig';

const toSchemaArray = (structuredData) => {
    if (!structuredData) return [];
    return Array.isArray(structuredData) ? structuredData : [structuredData];
};

const SEO = ({
    title = DEFAULT_SEO.title,
    description = DEFAULT_SEO.description,
    keywords = DEFAULT_SEO.keywords,
    path = '/',
    image = DEFAULT_SEO.image,
    imageAlt = SHARE_IMAGE_ALT,
    imageWidth = DEFAULT_SEO.imageWidth,
    imageHeight = DEFAULT_SEO.imageHeight,
    type = 'website',
    noindex = false,
    structuredData = null,
}) => {
    const fullTitle = buildPageTitle(title);
    const canonicalPath = normalizeCanonicalPath(path);
    const canonicalUrl = getCanonicalUrl(canonicalPath);
    const imageUrl = getAbsoluteImageUrl(image);
    const robots = noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';
    const schemas = toSchemaArray(structuredData).filter(Boolean);
    const imageType = imageUrl.endsWith('.png')
        ? 'image/png'
        : imageUrl.endsWith('.webp')
            ? 'image/webp'
            : 'image/jpeg';

    return (
        <Helmet>
            <html lang="pt-PT" />

            <title>{fullTitle}</title>
            <meta name="title" content={fullTitle} />
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="robots" content={robots} />
            <meta name="author" content={SITE_NAME} />
            <meta name="language" content="Portuguese" />
            <meta name="geo.region" content="PT" />
            <meta name="application-name" content={SITE_NAME} />
            <meta name="apple-mobile-web-app-title" content="Auto João Pires" />

            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:secure_url" content={imageUrl} />
            <meta property="og:image:type" content={imageType} />
            <meta property="og:image:width" content={String(imageWidth)} />
            <meta property="og:image:height" content={String(imageHeight)} />
            <meta property="og:image:alt" content={imageAlt} />
            <meta property="og:locale" content={DEFAULT_SEO.locale} />
            <meta property="og:site_name" content={SITE_NAME} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={TWITTER_HANDLE} />
            <meta name="twitter:creator" content={TWITTER_HANDLE} />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />
            <meta name="twitter:image:alt" content={imageAlt} />

            <meta property="instagram:site" content={INSTAGRAM_HANDLE} />

            <link rel="canonical" href={canonicalUrl} />
            <link rel="alternate" hrefLang="pt-PT" href={canonicalUrl} />
            <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

            {schemas.map((schema, index) => (
                <script key={index} type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    );
};

export default SEO;
